import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link2,
  RemoveFormatting,
} from 'lucide-react'
import { sanitizeEditorHtml } from '../../utils/sanitizeEditorHtml'

const TOOLBAR = [
  { icon: Bold, command: 'bold', titleKey: 'admin.editor.bold' },
  { icon: Italic, command: 'italic', titleKey: 'admin.editor.italic' },
  { icon: Underline, command: 'underline', titleKey: 'admin.editor.underline' },
  { icon: List, command: 'insertUnorderedList', titleKey: 'admin.editor.bulletList' },
  { icon: ListOrdered, command: 'insertOrderedList', titleKey: 'admin.editor.numberedList' },
]

const PAGE_COLORS = [
  { id: 'text', color: '#1a1a1a', titleKey: 'admin.editor.colorText' },
  { id: 'primary', color: '#5d1038', titleKey: 'admin.editor.colorPrimary' },
  { id: 'accent', color: '#f39200', titleKey: 'admin.editor.colorAccent' },
]

export default function RichTextEditor({
  value = '',
  onChange,
  placeholder,
  className = '',
  minHeight = '160px',
  dir,
  variant = 'default',
}) {
  const { t } = useTranslation()
  const editorRef = useRef(null)
  const syncingRef = useRef(false)
  const isPageVariant = variant === 'page'

  useEffect(() => {
    if (!editorRef.current || syncingRef.current) return
    const html = isPageVariant ? sanitizeEditorHtml(value || '') : value || ''
    if (editorRef.current.innerHTML !== html) {
      editorRef.current.innerHTML = html
    }
  }, [value, isPageVariant])

  const emitChange = useCallback(() => {
    if (!editorRef.current || !onChange) return
    syncingRef.current = true
    const html = isPageVariant
      ? sanitizeEditorHtml(editorRef.current.innerHTML)
      : editorRef.current.innerHTML

    if (isPageVariant && html !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = html
    }

    onChange(html)
    requestAnimationFrame(() => {
      syncingRef.current = false
    })
  }, [onChange, isPageVariant])

  const runCommand = (command, arg = null) => {
    editorRef.current?.focus()
    document.execCommand(command, false, arg)
    emitChange()
  }

  const addLink = () => {
    const url = window.prompt('URL')
    if (url) runCommand('createLink', url)
  }

  const applyColor = (color) => {
    editorRef.current?.focus()
    document.execCommand('foreColor', false, color)
    emitChange()
  }

  const handlePaste = (event) => {
    if (!isPageVariant) return

    event.preventDefault()
    const html = event.clipboardData.getData('text/html')
    const text = event.clipboardData.getData('text/plain')

    if (html) {
      document.execCommand('insertHTML', false, sanitizeEditorHtml(html))
    } else {
      document.execCommand('insertText', false, text)
    }

    emitChange()
  }

  return (
    <div className={`overflow-hidden rounded-xl border border-border bg-white ${className}`}>
      <div className="flex flex-wrap items-center gap-1 border-b border-border bg-background px-2 py-2">
        {TOOLBAR.map(({ icon: Icon, command, titleKey }) => (
          <button
            key={command}
            type="button"
            title={t(titleKey)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(command)}
            className="rounded-lg p-2 text-text/70 transition-colors hover:bg-white hover:text-primary"
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
        <button
          type="button"
          title={t('admin.editor.link')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={addLink}
          className="rounded-lg p-2 text-text/70 transition-colors hover:bg-white hover:text-primary"
        >
          <Link2 className="h-4 w-4" />
        </button>
        <button
          type="button"
          title={t('admin.editor.clearFormatting')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => runCommand('removeFormat')}
          className="rounded-lg p-2 text-text/70 transition-colors hover:bg-white hover:text-primary"
        >
          <RemoveFormatting className="h-4 w-4" />
        </button>

        {isPageVariant ? (
          <>
            <span className="mx-1 hidden h-6 w-px bg-border sm:inline" aria-hidden />
            <span className="px-1 text-xs font-medium text-text/50">{t('admin.editor.color')}</span>
            {PAGE_COLORS.map(({ id, color, titleKey }) => (
              <button
                key={id}
                type="button"
                title={t(titleKey)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyColor(color)}
                className="rounded-lg p-1.5 transition-colors hover:bg-white"
              >
                <span
                  className="block h-5 w-5 rounded-full border border-border/80 shadow-sm"
                  style={{ backgroundColor: color }}
                />
              </button>
            ))}
          </>
        ) : null}
      </div>

      <div
        ref={editorRef}
        contentEditable
        dir={dir}
        lang={dir === 'rtl' ? 'ar' : 'en'}
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
        className={`rich-text-editor cms-html px-4 py-3 outline-none ${
          isPageVariant
            ? 'page-content max-w-none bg-background'
            : 'text-sm leading-relaxed text-text'
        } ${dir === 'rtl' ? 'font-ar' : ''}`}
        style={{ minHeight: isPageVariant ? minHeight || '280px' : minHeight }}
        suppressContentEditableWarning
      />
    </div>
  )
}
